import { SkuParser } from '@jouzen/ecom-commons';

import { type Partner } from '@/configs/partner';
import { logger as browserLogger } from '@/logger/logger.browser';
import { Address } from '@/types';
import { ApiError } from '@/types/Error';
import { OrderLineItem, OrderStatus, PaymentMethod } from '@/types/Order';
import { metricIncrement } from '@/utils/reportMetrics';

import {
  LOGIN_METRIC_NAME,
  MetricStep,
} from '../components/ui/EmailForm/const';

type BaseOrder = {
  salesOrderId: string;
  netsuiteOrderId?: string;
  shippingAddress?: Address;
  orderDate: string;
  customerId?: string;
  channel?: string;
  orderStatus: OrderStatus;
  paymentMethod?: PaymentMethod;
};

type GetOrderLineItem = {
  skuCode: string;
  quantity?: number;
  price?: number;
  discount?: number;
};

interface GetOrderApiResponse extends BaseOrder {
  lineItems: GetOrderLineItem[];
}

interface GetOrders extends BaseOrder {
  lineItems: OrderLineItem[];
}

export type GetOrdersResponse = {
  data?: GetOrders[];
  error?: ApiError | unknown;
};

export async function getOrders(
  email: string,
  partner: Partner,
): Promise<GetOrdersResponse> {
  try {
    const resp = await fetch('/api/getOrders', {
      method: 'POST',
      body: JSON.stringify({ email, partner }),
    });
    const responseJson = await resp.json();
    if (responseJson?.error) {
      metricIncrement(LOGIN_METRIC_NAME, {
        step: MetricStep.GET_ORDERS_ERROR,
        partner,
      });

      browserLogger.warn('EOP Get Orders error', {
        partner,
        error: responseJson?.error ?? null,
      });

      return {
        error: responseJson?.error,
      };
    } else {
      metricIncrement(LOGIN_METRIC_NAME, {
        step: MetricStep.GET_ORDERS_SUCCESS,
        partner,
      });
      return {
        data: responseJson?.map((order: GetOrderApiResponse) => {
          return {
            ...order,
            lineItems: order.lineItems?.map((item: GetOrderLineItem) => {
              const parsedSku = new SkuParser(item.skuCode);
              // Set the sku if the ring selections were completed
              const sku = parsedSku.hasSize() ? item.skuCode : undefined;
              return {
                originalSku: item.skuCode,
                quantity: item.quantity,
                sku: sku,
                ...(item.price != null && { price: item.price }),
                ...(item.discount != null && { discount: item.discount }),
              };
            }),
          };
        }),
      };
    }
  } catch (error) {
    browserLogger.warn('Error calling getOrders', { error });
    return { error };
  }
}
